import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Runs an async fetcher and exposes { data, error, loading, reload, setData }.
 *
 * Handles the two things every screen in this app needs:
 *  - ignore results from a request that has been superseded or unmounted
 *  - re-fetch when `deps` change
 *
 * @param {(signal: AbortSignal) => Promise<T>} fetcher
 * @param {unknown[]} deps
 * @param {{ enabled?: boolean, initialData?: T }} [options]
 */
export function useApi(fetcher, deps = [], { enabled = true, initialData = null } = {}) {
  const [data, setData] = useState(initialData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);

  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (mounted.current) setData(result);
      return result;
    } catch (err) {
      if (mounted.current) setError(err);
      return null;
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, reload: run, setData };
}

/**
 * Wraps a one-shot mutation (create/update/delete) with pending + error state so
 * buttons can disable themselves without every page re-implementing it.
 *
 * @param {(...args: any[]) => Promise<any>} mutation
 */
export function useMutation(mutation) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const mutationRef = useRef(mutation);
  useEffect(() => {
    mutationRef.current = mutation;
  });

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const execute = useCallback(async (...args) => {
    setPending(true);
    setError(null);
    try {
      return await mutationRef.current(...args);
    } catch (err) {
      if (mounted.current) setError(err);
      throw err;
    } finally {
      if (mounted.current) setPending(false);
    }
  }, []);

  return { execute, pending, error, setError };
}

export default useApi;
